import React, { useState } from 'react'
import { FaChartLine } from 'react-icons/fa'
import {
  StepCard,
  StepHeader,
  StepContent,
  ScrollableContent,
  ProgressBar,
  StatCard,
} from './ModelTrainingView.styles'
import { Model } from '../../../types/models/model'
import { TrainingStatsBBox } from '../../../types/models/model'
import BBoxPerformanceMetricsChart from './BBoxPerformanceMetricsChart'
import LossValuesChart from './LossValuesChart'
import ErrorDialog from '../../modals/ErrorDialog'

// Define the structure of BBox training data
interface BBoxKeyValuePair {
  Key: string
  Value: number | string | BBoxKeyValuePair[] | BBoxTrainingJobEntry[]
}

// Type for the nested array structure in BBox training data
type BBoxTrainingJobEntry = BBoxKeyValuePair[]

interface BBoxTrainingProgressCardProps {
  model: Model | undefined
  gradient: string
}

const BBoxTrainingProgressCard: React.FC<BBoxTrainingProgressCardProps> = ({ model, gradient }) => {
  const [error, setError] = useState<string>('')
  const [showError, setShowError] = useState(false)

  const getCurrentEpoch = () => {
    const trainingStats = model?.training_stats?.bounding_box as TrainingStatsBBox
    if (!trainingStats?.training_job_metrics) return 0

    const firstJobKey = Object.keys(trainingStats.training_job_metrics)[0]
    const firstJob = trainingStats.training_job_metrics[firstJobKey]
    if (!firstJob?.training_job_entries || firstJob.training_job_entries.length === 0) return 0

    const lastEntry = firstJob.training_job_entries[firstJob.training_job_entries.length - 1]
    return (lastEntry.epoch || 0) + 1
  }

  const getTotalEpochs = () => {
    try {
      type EpochParam = { value?: unknown }
      const trainingParams =
        (model?.parameters?.hyperparameters?.hyperparameters?.training as
          | Record<string, EpochParam>
          | undefined) || undefined
      const epochs = trainingParams?.epochs as EpochParam | undefined

      if (epochs && typeof epochs === 'object' && 'value' in epochs) {
        const value = epochs.value
        return typeof value === 'number' ? value : 0
      }

      return 0
    } catch (err: unknown) {
      let message = 'Error retrieving epoch data'
      if (typeof err === 'object' && err !== null) {
        const maybeData = (err as { data?: { message?: unknown } }).data
        if (maybeData && typeof maybeData.message === 'string') {
          message = maybeData.message
        }
      }
      setError(message)
      setShowError(true)
      return 0
    }
  }

  const getProgressPercentage = () => {
    const currentEpoch = getCurrentEpoch()
    const totalEpochs = getTotalEpochs()

    if (totalEpochs === 0) return 0
    const percent = Math.round((currentEpoch / totalEpochs) * 100)
    if (model?.state === 'TRAINING' && percent >= 100) {
      return 90
    }
    return percent
  }

  const getStatusText = () => {
    if (!model?.training_stats?.bounding_box) return 'No training data available'

    const trainingStats = model.training_stats.bounding_box as TrainingStatsBBox
    if (trainingStats?.training_progress === 1) {
      return 'Training Completed'
    }
    if (!trainingStats?.training_job_metrics) return 'No training data available'

    const firstJobKey = Object.keys(trainingStats.training_job_metrics)[0]
    const firstJob = trainingStats.training_job_metrics[firstJobKey]
    const status = firstJob?.training_job_status

    if (!status) {
      return (
        <div className="d-flex align-items-center gap-2">
          Pending
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )
    }

    return status
  }

  const getProgressText = () => {
    return `${getProgressPercentage()}%`
  }

  const latestEntry = model?.training_stats?.bounding_box
    ? (() => {
        const trainingStats = model.training_stats.bounding_box as TrainingStatsBBox
        if (!trainingStats?.training_job_metrics) return null

        const firstJobKey = Object.keys(trainingStats.training_job_metrics)[0]
        const firstJob = trainingStats.training_job_metrics[firstJobKey]
        if (!firstJob?.training_job_entries || firstJob.training_job_entries.length === 0)
          return null

        const lastEntry = firstJob.training_job_entries[firstJob.training_job_entries.length - 1]

        return {
          metrics_precision: lastEntry.metrics_precision || 0,
          metrics_recall: lastEntry.metrics_recall || 0,
          metrics_map_50: lastEntry.metrics_map_50 || 0,
          validation_box_loss: lastEntry.validation_box_loss || 0,
          validation_class_loss: lastEntry.validation_class_loss || 0,
          validation_dfl_loss: lastEntry.validation_dfl_loss || 0,
        }
      })()
    : null

  return (
    <StepCard className="h-100">
      <StepHeader $gradient={gradient}>
        <FaChartLine />
        {`Training Progress${
          model?.training_stats?.bounding_box?.training_progress === 1 ? ' - Completed' : ''
        }`}
      </StepHeader>
      <StepContent>
        <ScrollableContent>
          {model?.training_stats ? (
            <>
              <div className="mb-4" style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1, display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#fff',
                        }}
                      >
                        {getStatusText()}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#4dabf7' }}>
                        {getProgressText()}
                      </div>
                    </div>
                    <ProgressBar $progress={getProgressPercentage()} />
                  </div>
                </div>
                <div style={{ width: '180px' }}>
                  <StatCard
                    style={{
                      padding: '0.5rem',
                      margin: 0,
                      minWidth: '100px',
                      height: 'fit-content',
                    }}
                  >
                    <div className="stat-number" style={{ fontSize: '1.1rem', marginBottom: 0 }}>
                      {getCurrentEpoch()} / {getTotalEpochs()}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Epochs
                    </div>
                  </StatCard>
                </div>
              </div>

              <div className="charts-container">
                <div className="d-flex gap-3 mb-3">
                  <div style={{ flex: 1 }}>
                    <BBoxPerformanceMetricsChart model={model} />
                  </div>
                  <div
                    style={{
                      width: '180px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <h6
                      style={{
                        fontSize: '0.8rem',
                        color: '#ffffff',
                        marginBottom: '0.5rem',
                      }}
                    >
                      Latest Metrics
                    </h6>
                    {latestEntry && (
                      <div
                        className="d-grid gap-2"
                        style={{
                          height: '250px',
                          gridTemplateRows: 'repeat(3, 1fr)',
                        }}
                      >
                        <StatCard
                          style={{
                            padding: '0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            className="stat-number"
                            style={{
                              fontSize: '1.1rem',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {(latestEntry.metrics_precision * 100).toFixed(1)}%
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Precision
                          </div>
                        </StatCard>
                        <StatCard
                          style={{
                            padding: '0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            className="stat-number"
                            style={{
                              fontSize: '1.1rem',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {(latestEntry.metrics_recall * 100).toFixed(1)}%
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Recall
                          </div>
                        </StatCard>
                        <StatCard
                          style={{
                            padding: '0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            className="stat-number"
                            style={{
                              fontSize: '1.1rem',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {(latestEntry.metrics_map_50 * 100).toFixed(1)}%
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            mAP50
                          </div>
                        </StatCard>
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex gap-3">
                  <div style={{ flex: 1 }}>
                    <LossValuesChart model={model} />
                  </div>
                  <div
                    style={{
                      width: '180px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <h6
                      style={{
                        fontSize: '0.8rem',
                        color: '#ffffff',
                        marginBottom: '0.5rem',
                      }}
                    >
                      Latest Loss Values
                    </h6>
                    {latestEntry && (
                      <div
                        className="d-grid gap-2"
                        style={{
                          height: '250px',
                          gridTemplateRows: 'repeat(3, 1fr)',
                        }}
                      >
                        <StatCard
                          style={{
                            padding: '0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            className="stat-number"
                            style={{
                              fontSize: '1.1rem',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {latestEntry.validation_box_loss.toFixed(4)}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Box Loss
                          </div>
                        </StatCard>
                        <StatCard
                          style={{
                            padding: '0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            className="stat-number"
                            style={{
                              fontSize: '1.1rem',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {latestEntry.validation_class_loss.toFixed(4)}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Class Loss
                          </div>
                        </StatCard>
                        <StatCard
                          style={{
                            padding: '0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            className="stat-number"
                            style={{
                              fontSize: '1.1rem',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {latestEntry.validation_dfl_loss.toFixed(4)}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            DFL Loss
                          </div>
                        </StatCard>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100 text-muted">
              No training data available
            </div>
          )}
        </ScrollableContent>
      </StepContent>

      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={error}
        title="Data Error"
      />
    </StepCard>
  )
}

export default BBoxTrainingProgressCard
